import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BarChart3, Clock, MousePointer2, RefreshCcw, ThumbsUp, Users, Zap } from 'lucide-react';
import type { AppDispatch, RootState } from '@/app/store';
import { selectCurrentWorkspace } from '@/features/workspace/workspaceSlice';
import { fetchComparisonData } from '../store/analyticsSlice';
import { selectComparison } from '../store/analyticSelector';
import { ComparisonTimelineChart } from '../components/ComparisonTimelineChart';
import { ComparisonDeviceCharts } from '../components/ComparisonDeviceCharts';
import { WebinarStatusTag } from '../components/WebinarStatusTag';
import { WebinarSelectionModal } from '../components/WebinarSelectionModal';
import { Button } from '@/components/ui/button';
import { cn, getLocale } from '@/lib/utils';
import { useSearchParams } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const AnalyticsComparator = () => {
  const { t } = useTranslation('analytics');
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const workspace = useSelector(selectCurrentWorkspace);
  const { data, loading } = useSelector(selectComparison);

  const [slotA, setSlotA] = useState<any | null>(null);
  const [slotB, setSlotB] = useState<any | null>(null);
  const [activeMetric, setActiveMetric] = useState<
    'presence' | 'messages' | 'clicks' | 'quiz' | 'surveys'
  >('presence');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSlot, setActiveSlot] = useState<'A' | 'B' | null>(null);
  const { list: webinars } = useSelector((state: RootState) => state.analytics.pastWebinars);

  useEffect(() => {
    const sessionAId = searchParams.get('sessionAId');
    if (sessionAId && webinars.length > 0 && !slotA) {
      const foundSession = webinars.find((w: any) => w.sessionId === Number(sessionAId));
      if (foundSession) {
        setSlotA(foundSession);
      }
    }
  }, [webinars, searchParams, slotA]);

  useEffect(() => {
    if (workspace?.id && slotA?.sessionId && slotB?.sessionId) {
      dispatch(
        fetchComparisonData({
          workspaceId: workspace.id,
          sessionIdA: slotA.sessionId,
          sessionIdB: slotB.sessionId,
        }),
      );
    }
  }, [dispatch, workspace?.id, slotA, slotB]);

  const openSelection = (slot: 'A' | 'B') => {
    setActiveSlot(slot);
    setIsModalOpen(true);
  };

  const handleReset = () => {
    setSlotA(null);
    setSlotB(null);
  };

  /**
   * Détermine le gagnant entre deux valeurs.
   * Retourne 'BOTH' si les deux sont égaux, permettant de mettre les deux en avant.
   */
  const resolveWinner = (valA: number | string, valB: number | string): 'A' | 'B' | 'BOTH' => {
    const numA =
      typeof valA === 'string' ? Number.parseFloat(valA.replaceAll(/[^\d.]/g, '')) : valA;
    const numB =
      typeof valB === 'string' ? Number.parseFloat(valB.replaceAll(/[^\d.]/g, '')) : valB;

    if (numA === numB) return 'BOTH';
    return numA > numB ? 'A' : 'B';
  };

  return (
    <div className="flex flex-col gap-8 w-full pb-20 font-poppins">
      <div className="flex items-center justify-between px-2">
        <h2 className="text-2xl font-bold text-dark">{t('comparator.title')}</h2>
        <Button
          variant="ghost"
          onClick={handleReset}
          className="text-role-tertiary hover:text-dark"
        >
          <RefreshCcw size={16} className="mr-2" /> {t('comparator.reset')}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FigmaSelectionCard
          label={t('comparator.webinarA')}
          webinar={slotA}
          presenters={data?.headers.sessionA.presenters}
          onSelect={() => openSelection('A')}
          color="blue"
        />
        <FigmaSelectionCard
          label={t('comparator.webinarB')}
          webinar={slotB}
          presenters={data?.headers.sessionB.presenters}
          onSelect={() => openSelection('B')}
          color="orange"
          isSlotB
        />
      </div>

      {!data && !loading && (
        <div className="py-40 text-center border-2 border-dashed border-medium rounded-[32px] bg-[#fafafa]">
          <BarChart3 className="mx-auto size-12 text-[#cecfd3] mb-4" />
          <p className="text-role-tertiary font-medium italic">{t('comparator.empty.hint')}</p>
        </div>
      )}

      {loading && (
        <div className="py-40 flex flex-col items-center gap-4">
          <div className="size-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-role-tertiary font-medium">{t('comparator.loading')}</p>
        </div>
      )}

      {data && !loading && (
        <>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between px-2">
              <h3 className="text-xl font-bold text-dark">{t('comparator.audienceTitle')}</h3>
              <Select value={activeMetric} onValueChange={(v: any) => setActiveMetric(v)}>
                <SelectTrigger className="w-[200px] bg-white border-neutral-100 rounded-xl">
                  <SelectValue placeholder={t('comparator.metricPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="presence">{t('comparator.metrics.presence')}</SelectItem>
                  <SelectItem value="messages">{t('comparator.metrics.messages')}</SelectItem>
                  <SelectItem value="clicks">{t('comparator.metrics.clicks')}</SelectItem>
                  <SelectItem value="quiz">{t('comparator.metrics.quiz')}</SelectItem>
                  <SelectItem value="surveys">{t('comparator.metrics.surveys')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ComparisonTimelineChart data={data.timeline} activeMetric={activeMetric} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* COLUMN A */}
            <div className="flex flex-col gap-6">
              <SessionInfoCard
                title={t('comparator.webinarA')}
                data={data.headers.sessionA}
                color="#2c6cff"
                timezone={workspace?.timezone}
              />

              <ComparisonKpiBlock
                title={t('comparator.kpi.acquisition')}
                stats={[
                  {
                    label: t('comparator.kpi.registrants'),
                    value: data.summaries.sessionA.registrantsCount,
                    Icon: Users,
                    isWinner: ['A', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.registrantsCount,
                        data.summaries.sessionB.registrantsCount,
                      ),
                    ),
                  },
                  {
                    label: t('comparator.kpi.participants'),
                    value: data.summaries.sessionA.participantsCount,
                    Icon: Users,
                    isWinner: ['A', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.participantsCount,
                        data.summaries.sessionB.participantsCount,
                      ),
                    ),
                  },
                  {
                    label: t('comparator.kpi.averageWatchTime'),
                    value: data.summaries.sessionA.averageWatchTime,
                    Icon: Clock,
                    isWinner: ['A', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.averageWatchTime,
                        data.summaries.sessionB.averageWatchTime,
                      ),
                    ),
                  },
                ]}
              />

              <ComparisonKpiBlock
                title={t('comparator.kpi.engagement')}
                stats={[
                  {
                    label: t('comparator.kpi.activeParticipation'),
                    value: `${data.summaries.sessionA.activeParticipationRate}%`,
                    Icon: MousePointer2,
                    isWinner: ['A', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.activeParticipationRate,
                        data.summaries.sessionB.activeParticipationRate,
                      ),
                    ),
                  },
                  {
                    label: t('comparator.kpi.offerClicks'),
                    value: data.summaries.sessionA.offerClicksCount,
                    Icon: ThumbsUp,
                    isWinner: ['A', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.offerClicksCount,
                        data.summaries.sessionB.offerClicksCount,
                      ),
                    ),
                  },
                ]}
              />

              <ComparisonKpiBlock
                title={t('comparator.kpi.conversion')}
                stats={[
                  {
                    label: t('comparator.kpi.conversions'),
                    value: data.summaries.sessionA.salesCount,
                    Icon: Zap,
                    isWinner: ['A', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.salesCount,
                        data.summaries.sessionB.salesCount,
                      ),
                    ),
                  },
                  {
                    label: t('comparator.kpi.revenue'),
                    value: `${data.summaries.sessionA.totalRevenueAmount}€`,
                    Icon: BarChart3,
                    isWinner: ['A', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.totalRevenueAmount,
                        data.summaries.sessionB.totalRevenueAmount,
                      ),
                    ),
                  },
                ]}
              />
            </div>

            {/* COLUMN B */}
            <div className="flex flex-col gap-6">
              <SessionInfoCard
                title={t('comparator.webinarB')}
                data={data.headers.sessionB}
                color="#ff6526"
                timezone={workspace?.timezone}
              />

              <ComparisonKpiBlock
                title={t('comparator.kpi.acquisition')}
                stats={[
                  {
                    label: t('comparator.kpi.registrants'),
                    value: data.summaries.sessionB.registrantsCount,
                    Icon: Users,
                    isWinner: ['B', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.registrantsCount,
                        data.summaries.sessionB.registrantsCount,
                      ),
                    ),
                  },
                  {
                    label: t('comparator.kpi.participants'),
                    value: data.summaries.sessionB.participantsCount,
                    Icon: Users,
                    isWinner: ['B', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.participantsCount,
                        data.summaries.sessionB.participantsCount,
                      ),
                    ),
                  },
                  {
                    label: t('comparator.kpi.averageWatchTime'),
                    value: data.summaries.sessionB.averageWatchTime,
                    Icon: Clock,
                    isWinner: ['B', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.averageWatchTime,
                        data.summaries.sessionB.averageWatchTime,
                      ),
                    ),
                  },
                ]}
              />

              <ComparisonKpiBlock
                title={t('comparator.kpi.engagement')}
                stats={[
                  {
                    label: t('comparator.kpi.activeParticipation'),
                    value: `${data.summaries.sessionB.activeParticipationRate}%`,
                    Icon: MousePointer2,
                    isWinner: ['B', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.activeParticipationRate,
                        data.summaries.sessionB.activeParticipationRate,
                      ),
                    ),
                  },
                  {
                    label: t('comparator.kpi.offerClicks'),
                    value: data.summaries.sessionB.offerClicksCount,
                    Icon: ThumbsUp,
                    isWinner: ['B', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.offerClicksCount,
                        data.summaries.sessionB.offerClicksCount,
                      ),
                    ),
                  },
                ]}
              />

              <ComparisonKpiBlock
                title={t('comparator.kpi.conversion')}
                stats={[
                  {
                    label: t('comparator.kpi.conversions'),
                    value: data.summaries.sessionB.salesCount,
                    Icon: Zap,
                    isWinner: ['B', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.salesCount,
                        data.summaries.sessionB.salesCount,
                      ),
                    ),
                  },
                  {
                    label: t('comparator.kpi.revenue'),
                    value: `${data.summaries.sessionB.totalRevenueAmount}€`,
                    Icon: BarChart3,
                    isWinner: ['B', 'BOTH'].includes(
                      resolveWinner(
                        data.summaries.sessionA.totalRevenueAmount,
                        data.summaries.sessionB.totalRevenueAmount,
                      ),
                    ),
                  },
                ]}
              />
            </div>
          </div>
          <ComparisonDeviceCharts dataA={data.technical.sessionA} dataB={data.technical.sessionB} />
        </>
      )}

      <WebinarSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={(webinar) => (activeSlot === 'A' ? setSlotA(webinar) : setSlotB(webinar))}
        excludeId={activeSlot === 'A' ? slotB?.sessionId : slotA?.sessionId}
      />
    </div>
  );
};

const FigmaSelectionCard = ({ label, webinar, presenters, onSelect, color, isSlotB }: any) => {
  const { t } = useTranslation('analytics');
  return (
    <div
      className={cn(
        'relative rounded-[32px] border-2 transition-all duration-300 h-[240px] flex flex-col overflow-hidden',
        webinar
          ? 'bg-white border-neutral-100 shadow-sm'
          : 'bg-neutral-50 border-dashed border-neutral-200',
      )}
    >
      <div className="py-3 bg-neutral-50/50 border-b border-neutral-100 text-center text-[10px] font-bold text-secondary uppercase tracking-widest">
        {label}
      </div>
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
        {webinar ? (
          <>
            <div className="flex -space-x-3 mb-4">
              {presenters?.length > 0 ? (
                presenters.map((presenter: any, index: number) => (
                  <Avatar key={index} className="size-12 border-4 border-white shadow-sm">
                    <AvatarImage src={presenter.pictureUrl || ''} />
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold uppercase">
                      {presenter.fullName?.charAt(0) || 'P'}
                    </AvatarFallback>
                  </Avatar>
                ))
              ) : (
                <div
                  className={cn(
                    'size-12 rounded-full flex items-center justify-center',
                    color === 'blue'
                      ? 'bg-primary/10 text-primary'
                      : 'bg-orange-500/10 text-orange-500',
                  )}
                >
                  <Users size={24} />
                </div>
              )}
            </div>
            <h3 className="text-xl font-bold text-dark leading-tight mb-1 truncate w-full px-4">
              {webinar.title}
            </h3>
            <p className="text-xs text-secondary font-medium uppercase tracking-tight">
              {new Date(webinar.projectionDate).toLocaleDateString(getLocale())} •{' '}
              {webinar.participantsCount} {t('comparator.registrantsCount')}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={onSelect}
              className="mt-4 text-xs font-bold h-8 text-primary hover:bg-primary/5"
            >
              {t('comparator.changeSelection')}
            </Button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <p className="text-secondary font-medium text-sm leading-relaxed">
              {isSlotB ? t('comparator.selectSecond') : t('comparator.selectFirst')} <br />{' '}
              {t('comparator.selectSuffix')}
            </p>
            <Button
              onClick={onSelect}
              variant="outline"
              className="rounded-full px-8 border-dark text-dark hover:bg-dark hover:text-white transition-all"
            >
              {t('comparator.select')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const ComparisonKpiBlock = ({ title, stats }: { title: string; stats: any[] }) => (
  <div className="bg-[#fafafa] border border-neutral-100 rounded-[24px] p-6 flex flex-col gap-4">
    <h4 className="text-sm font-bold text-secondary uppercase tracking-widest mb-2 px-1">
      {title}
    </h4>
    <div className="flex flex-col gap-3">
      {stats.map((s, i) => (
        <div
          key={i}
          className={cn(
            'bg-white border-2 rounded-[16px] p-5 flex justify-between items-center shadow-sm transition-all duration-300',
            s.isWinner
              ? 'border-primary shadow-[0_8px_24px_rgba(44,108,255,0.12)] scale-[1.02] z-10'
              : 'border-neutral-50 opacity-60',
          )}
        >
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-secondary uppercase tracking-widest mb-1">
              {s.label}
            </span>
            <span
              className={cn(
                'text-2xl font-bold font-poppins',
                s.isWinner ? 'text-primary' : 'text-dark',
              )}
            >
              {s.value}
            </span>
          </div>
          <div
            className={cn(
              'p-3 rounded-2xl transition-colors',
              s.isWinner ? 'bg-primary/10 text-primary' : 'bg-neutral-50 text-secondary',
            )}
          >
            <s.Icon className="size-6" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SessionInfoCard = ({ title, data, color, timezone }: any) => {
  const { t } = useTranslation('analytics');
  return (
    <div className="bg-white border border-medium rounded-[24px] overflow-hidden shadow-sm flex flex-col h-full">
      <div
        className="px-6 py-4 border-b border-medium flex items-center justify-center"
        style={{ backgroundColor: color + '10' }}
      >
        <span className="text-sm font-bold uppercase tracking-widest" style={{ color }}>
          {title}
        </span>
      </div>
      <div className="p-8 flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-1">
          <WebinarStatusTag sessionType={data.type} />
          <h3 className="text-2xl font-bold text-dark leading-tight mt-2">{data.title}</h3>
          <p className="text-sm text-role-tertiary font-medium uppercase">
            {new Date(data.projectionDate).toLocaleDateString(getLocale(), {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              timeZone: timezone || 'Europe/Paris',
            })}
          </p>
        </div>
        <div className="flex items-center gap-3 mt-2 border-t border-neutral-50 pt-4">
          <div className="flex -space-x-2">
            {data.presenters?.map((p: any, i: number) => (
              <Avatar key={i} className="size-8 border-2 border-white shadow-sm">
                <AvatarImage src={p.pictureUrl} />
                <AvatarFallback className="text-[8px] font-bold">
                  {p.fullName?.charAt(0)}
                </AvatarFallback>
              </Avatar>
            ))}
          </div>
          <span className="text-[11px] font-semibold text-role-tertiary uppercase tracking-tight">
            {data.presenters?.length > 1
              ? t('comparator.presenters_other', { count: data.presenters.length })
              : data.presenters?.[0]?.fullName}
          </span>
        </div>
      </div>
    </div>
  );
};
